/**
 * Every packet unit on paper, by `kind` (the data is `lib/chefs/packet`).
 * Tables are flex rows with fixed-width cells, so each row is its own
 * measurable box and the columns still line up across rows and pages.
 * Column widths are static classes chosen by count: the PDF route keeps no
 * `style` attribute, and Tailwind only ships classes it can see in the source.
 */

import { cn } from '@ordo/ui/cn'
import { TriangleAlert } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'

import {
  BAND_SPACE,
  BAND_TITLE,
  DateBox,
  DietTags,
  rowPad,
  RowTags,
  RULE_BOTTOM,
  SectionBand,
  StopTile,
  StopTypeTag,
  TempTag,
  TickBox,
  usePacketRender,
  WriteIn,
} from '@/components/chefs/print/packet/chrome'
import type { ContainerLine, PacketUnit, StopCopy, StopIdentity, StopRowData } from '@/lib/chefs/packet/units'

function count(value: number | null): string {
  return value === null ? '–' : value.toLocaleString()
}

/** "**15x** Standard Hotel Pans w/ **20**"; a section prints indented under its package: "› **60x** Kraft Sandwich Bags — Muffin". */
function ContainerText({ line }: { line: ContainerLine }) {
  return (
    <span className={line.section ? "block pl-[8px] before:mr-[3px] before:content-['›']" : 'block'}>
      {line.count ? <span className="font-bold tabular-nums">{line.count} </span> : null}
      {line.name}
      {line.fill !== null ? (
        <>
          {' w/ '}
          <span className="font-bold tabular-nums">{line.fill}</span>
        </>
      ) : null}
    </span>
  )
}

/** P14: the organization in small caps over the site. */
function StopName({ stop }: { stop: StopIdentity }) {
  return (
    <span className="flex min-w-0 flex-col">
      {stop.organization ? (
        <span className="text-[10px] font-normal normal-case leading-tight tracking-normal">{stop.organization}</span>
      ) : null}
      <span className="text-[12px] font-semibold leading-tight">{stop.site}</span>
    </span>
  )
}

function Warning({ text }: { text: string }) {
  return (
    <span className="mt-1 inline-block self-start border-[1pt] border-[#1a1a1a] px-1.5 py-0.5 text-[10.5px] font-bold normal-case tracking-normal">
      {text}
    </span>
  )
}

// --- day cover ---------------------------------------------------------------

const COVER_COLS = {
  number: 'w-[44px] shrink-0',
  stop: 'min-w-0 flex-1',
  window: 'w-[150px] shrink-0',
  sections: 'w-[250px] shrink-0',
}

/**
 * An outlined box with a warning icon: the cover's ride-along line. `compact`: the tighter spacing a cover takes to
 * keep to one page (`preferOnePage`, a long run of stops).
 */
function CoverNotice({ label, text, compact }: Extract<PacketUnit, { kind: 'cover-notice' }> & { compact: boolean }) {
  return (
    <div className={compact ? 'pt-1.5' : 'pt-2.5'}>
      <div
        className={cn(
          'flex items-start gap-2.5 rounded-lg border-[1pt] border-[#1a1a1a] px-3.5 leading-[1.4]',
          compact ? 'py-1.5 text-[12.5px]' : 'py-2 text-[13px]',
        )}>
        <TriangleAlert aria-hidden className="mt-[2px] h-[16px] w-[16px] shrink-0" strokeWidth={2.2} />
        <p>
          <span className="font-bold">{label}:</span> {text}
        </p>
      </div>
    </div>
  )
}

function CoverStopsHead() {
  return (
    <div className={TABLE_HEADER_CLASS}>
      <span className={COVER_COLS.number}>#</span>
      <span className={COVER_COLS.stop}>Stop</span>
      <span className={COVER_COLS.window}>Window</span>
      <span className={COVER_COLS.sections}>Sections</span>
    </div>
  )
}

/**
 * A stop in delivery order: its badge, the site over the organization, the window, every section it takes.
 * `compact` (a cover with many stops): one line where it can, the organization after the site and the window note
 * after the window, a smaller badge, so 14 stops keep to the cover's page.
 */
function CoverStop({
  stop,
  window,
  windowNote,
  sections,
  warning,
  compact,
}: Extract<PacketUnit, { kind: 'cover-stop' }> & { compact: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center border-b-[0.5pt] border-[#bbbbbb] [&>span]:px-2',
        rowPad(compact),
        compact ? 'text-[12px]' : 'text-[13px]',
      )}>
      <span className={COVER_COLS.number}>
        <StopTile number={stop.number} size={compact ? 'md' : 'lg'} />
      </span>
      {compact ? (
        // One line: the site, then its organization cut short rather than wrapped (14 stops keep to one page).
        <span className={cn(COVER_COLS.stop, 'truncate leading-tight')}>
          <span className="text-[12.5px] font-bold">{stop.site}</span>
          {stop.organization ? <span className="text-[11px] text-[#555555]"> · {stop.organization}</span> : null}
          {warning ? <Warning text={warning} /> : null}
        </span>
      ) : (
        <span className={cn(COVER_COLS.stop, 'flex flex-col gap-0.5')}>
          <span className="text-[14px] font-bold leading-tight">{stop.site}</span>
          {stop.organization ? <span className="text-[12px] text-[#555555]">{stop.organization}</span> : null}
          {warning ? <Warning text={warning} /> : null}
        </span>
      )}
      <span className={cn(COVER_COLS.window, 'leading-tight', compact && 'text-[11.5px]')}>
        <span className="whitespace-nowrap font-bold">{window ?? '–'}</span>
        {windowNote ? (
          // Compact: a small second line, never run into the Sections column ("Lunch at 12:15 PM").
          <span className={cn('block text-[#555555]', compact ? 'text-[10px] leading-[1.15]' : 'text-[11.5px]')}>
            {windowNote}
          </span>
        ) : null}
      </span>
      <span className={cn(COVER_COLS.sections, compact ? 'text-[10.5px] leading-tight' : 'text-[12.5px] leading-snug')}>
        {sections.join(' · ')}
      </span>
    </div>
  )
}

/** "Prep Recipes (12)" and its pages, "p.4–15". */
function CoverToc({ label, sectionIds, compact }: Extract<PacketUnit, { kind: 'cover-toc' }> & { compact: boolean }) {
  const page = usePacketRender().pageOf(sectionIds)
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4 border-b-[0.5pt] border-[#bbbbbb]',
        rowPad(compact),
        compact ? 'text-[12px]' : 'text-[13px]',
      )}>
      <span>{label}</span>
      <span className="font-bold tabular-nums">{page ? `p.${page}` : ''}</span>
    </div>
  )
}

// --- pull & pack ---------------------------------------------------------------

const PULL_COLS = {
  item: 'min-w-0 flex-1',
  temp: 'w-[104px] shrink-0',
  stop: 'w-[46px] shrink-0',
  total: 'w-[60px] shrink-0',
  tick: 'w-[28px] shrink-0 flex',
}

/** The grid's key under the title: "1 OPM OLHSA Pontiac Main Building · 2 HIL …". */
function PullKey({ stops }: Extract<PacketUnit, { kind: 'pull-key' }>) {
  return (
    <p className="pt-2 text-[12px] leading-[1.6] text-[#555555]">
      {stops.map((stop, i) => (
        <Fragment key={stop.number}>
          {i > 0 ? ' · ' : null}
          <span className="font-bold text-[#1a1a1a]">
            {stop.number} {stop.code}
          </span>{' '}
          {stop.site}
        </Fragment>
      ))}
    </p>
  )
}

/** The Pull List's one step: pull to the totals, then pack stop by stop from the kitchen sheets. */
function PullIntro({ sheetIds }: Extract<PacketUnit, { kind: 'pull-intro' }>) {
  const pages = usePacketRender().pageOf(sheetIds)
  return (
    <div className="pt-2.5">
      <div className="flex items-center gap-4 rounded-lg border-[1pt] border-[#1a1a1a] px-3.5 py-2 text-[13px] leading-[1.4]">
        <span className="flex shrink-0 flex-col border-r-[0.5pt] border-[#bbbbbb] pr-4">
          <span className="text-[12px] font-bold tracking-[1px]">STEP 1</span>
          <span className="text-[22px] font-extrabold leading-[1.1]">Pull</span>
        </span>
        <span>
          Pull every item below to the totals, then{' '}
          <span className="font-bold">pack stop by stop from each kitchen sheet{pages ? ` (p.${pages})` : ''}</span>.
        </span>
      </div>
    </div>
  )
}

function PullHead({ stops }: Extract<PacketUnit, { kind: 'pull-head' }>) {
  return (
    <div className={TABLE_HEADER_CLASS}>
      <span className={PULL_COLS.item}>Item · Pack</span>
      <span className={PULL_COLS.temp}>Deliver temp</span>
      {(stops ?? []).map((stop) => (
        <span key={stop.number} className={cn(PULL_COLS.stop, 'flex flex-col leading-tight')}>
          <span className="text-[#1a1a1a]">{stop.number}</span>
          <span className="text-[9.5px]">{stop.code}</span>
        </span>
      ))}
      <span className={PULL_COLS.total}>Total</span>
      <span className={PULL_COLS.tick}>✓</span>
    </div>
  )
}

function PullRow({ name, pack, temperature, counts, total }: Extract<PacketUnit, { kind: 'pull-row' }>) {
  return (
    <div className={cn('flex items-center border-b-[0.5pt] border-[#bbbbbb] text-[13px] [&>span]:px-2', rowPad())}>
      <span className={PULL_COLS.item}>
        <span className="font-bold">{name}</span>
        {pack ? <span className="text-[12px] text-[#555555]"> · {pack}</span> : null}
      </span>
      <span className={PULL_COLS.temp}>
        <TempTag temperature={temperature} />
      </span>
      {(counts ?? []).map((value, i) => (
        <span key={i} className={cn(PULL_COLS.stop, 'tabular-nums', value === null && 'text-[#555555]')}>
          {count(value)}
        </span>
      ))}
      <span className={cn(PULL_COLS.total, 'font-bold tabular-nums')}>{total.toLocaleString()}</span>
      <span className={PULL_COLS.tick}>
        <TickBox />
      </span>
    </div>
  )
}

/** A table's column labels. Every column, label and value alike, aligns left (Sheila, Oct 24), numbers included. */
const TABLE_HEADER_CLASS =
  'flex items-stretch [&>span]:whitespace-nowrap [&>span]:px-2 [&>span]:pb-1 [&>span]:pt-1 text-[11px] font-bold uppercase tracking-[0.6px] text-[#555555]'

// --- matrices --------------------------------------------------------------

/**
 * Production Totals' columns (the Oct 21 mockup): Item, Deliver temp, Portion, Total, Pack, Stops. Stops is wide
 * enough for a run like "1–5, 11–14" on one line (Oct 24: at 56px it wrapped to four lines and stretched the row).
 */
const PRODUCTION_COLS = {
  item: 'min-w-0 flex-1',
  temp: 'w-[92px] shrink-0',
  portion: 'w-[76px] shrink-0',
  total: 'w-[52px] shrink-0',
  pack: 'w-[188px] shrink-0',
  stops: 'w-[84px] shrink-0',
}

function MatrixHead({ space }: Extract<PacketUnit, { kind: 'matrix-head' }>) {
  return (
    <div className={space ? BAND_SPACE[space] : undefined}>
      <div className={TABLE_HEADER_CLASS}>
        <span className={PRODUCTION_COLS.item}>Item</span>
        <span className={PRODUCTION_COLS.temp}>Deliver temp</span>
        <span className={PRODUCTION_COLS.portion}>Portion</span>
        <span className={PRODUCTION_COLS.total}>Total</span>
        <span className={PRODUCTION_COLS.pack}>Pack</span>
        <span className={PRODUCTION_COLS.stops}>Stops</span>
      </div>
    </div>
  )
}

/** A Production Totals row (the Oct 21 mockup): one line per food, all stops combined. */
function MatrixRow({
  name,
  temperature,
  detail,
  diets,
  tags,
  total,
  packInstructions,
  stops,
}: Extract<PacketUnit, { kind: 'matrix-row' }>) {
  return (
    <div className="flex items-center border-b-[0.5pt] border-[#bbbbbb] text-[12.5px] leading-[1.3] [&>span]:px-2 [&>span]:py-1">
      <span className={PRODUCTION_COLS.item}>
        <span className="font-bold">{name}</span>
        <DietTags diets={diets} />
        <RowTags tags={tags} />
      </span>
      <span className={PRODUCTION_COLS.temp}>{temperature ? <TempTag temperature={temperature} /> : null}</span>
      <span className={PRODUCTION_COLS.portion}>{detail ?? '—'}</span>
      <span className={cn(PRODUCTION_COLS.total, 'font-semibold tabular-nums')}>{total.toLocaleString()}</span>
      <span className={cn(PRODUCTION_COLS.pack, 'text-[11px] leading-[1.35] text-[#555555]')}>
        {packInstructions.length
          ? packInstructions.map((line) => (
              <span key={line} className={line.startsWith('+ ') ? 'block italic' : 'block'}>
                {line}
              </span>
            ))
          : '—'}
      </span>
      <span className={cn(PRODUCTION_COLS.stops, 'tabular-nums')}>{stops}</span>
    </div>
  )
}

/** A section's closing line, with the page of what it points to: "... on the pull & pack list (p.9)." */
function SectionNote({ text, sectionIds }: Extract<PacketUnit, { kind: 'section-note' }>) {
  const page = usePacketRender().pageOf(sectionIds)
  return (
    <p className="pt-2.5 text-[12px] italic text-[#555555]">
      {text}
      {page ? ` (p.${page})` : ''}.
    </p>
  )
}

function MatrixSubtotal({ label, total }: Extract<PacketUnit, { kind: 'matrix-subtotal' }>) {
  return (
    <div className="flex items-stretch border-b-[0.5pt] border-[#bbbbbb] [&>span]:px-3 [&>span]:py-1 text-[11px]">
      <span className="min-w-0 flex-1">{label.replace(/^=\s*/, '')}</span>
      <span className="w-[120px] shrink-0 font-semibold tabular-nums">{total.toLocaleString()}</span>
      <span className="w-[280px] shrink-0" />
    </div>
  )
}

// --- shopping list -----------------------------------------------------------

/** Purchasing columns: item and quantities, without recipe or stop references. */
const SHOP_COLS = {
  tick: 'w-[16px] shrink-0',
  item: 'min-w-0 flex-1',
  buy: 'w-[120px] shrink-0',
}

// --- milk ------------------------------------------------------------------

/** Abner, Oct 19: both ways to buy a milk, at one weight ("show both options, with no prevalence to any"). */
const MILK_SHOP_COLS = {
  count: 'w-[120px] shrink-0',
}

function MilkShopHead() {
  return (
    <div className={TABLE_HEADER_CLASS}>
      <span className={SHOP_COLS.tick} />
      <span className={SHOP_COLS.item}>Item</span>
      <span className={MILK_SHOP_COLS.count} />
    </div>
  )
}

function MilkShopRow({ name, purchase }: Extract<PacketUnit, { kind: 'milk-shop-row' }>) {
  return (
    <div className={cn('flex items-center gap-2 border-b-[0.5pt] border-[#bbbbbb] px-3 text-[12px]', rowPad())}>
      <span className={SHOP_COLS.tick}>
        <TickBox size="sm" />
      </span>
      <span className={cn(SHOP_COLS.item, 'font-medium')}>{name}</span>
      <span className={cn(MILK_SHOP_COLS.count, 'font-semibold tabular-nums')}>{purchase ?? '–'}</span>
    </div>
  )
}

function MilkServed({ lines }: Extract<PacketUnit, { kind: 'milk-served' }>) {
  return (
    <div className="flex flex-col gap-0.5 pt-2 text-[12px]">
      {lines.map((line) => (
        <p key={line.label}>
          <span className="font-semibold">{line.label}:</span> {line.detail}
          {line.note ? <span className="font-semibold">. {line.note}</span> : null}
        </p>
      ))}
    </div>
  )
}

const MILK_PACK_COLS = {
  milk: 'min-w-0 flex-1',
  containers: 'w-[132px] shrink-0',
  total: 'w-[52px] shrink-0',
  done: 'w-[34px] shrink-0',
}

/** A serve time's column; narrower past three, so the milk keeps its room. */
function milkServeCol(columns: number): string {
  return columns > 3 ? 'w-[76px] shrink-0' : 'w-[96px] shrink-0'
}

function MilkPackHead({ columns }: Extract<PacketUnit, { kind: 'milk-pack-head' }>) {
  const serve = milkServeCol(columns.length)
  return (
    <div className="pt-2">
      <div className={TABLE_HEADER_CLASS}>
        <span className={MILK_PACK_COLS.milk}>Milk</span>
        <span className={MILK_PACK_COLS.containers}>Containers</span>
        {columns.map((label) => (
          <span key={label} className={serve}>
            {label}
          </span>
        ))}
        <span className={MILK_PACK_COLS.total}>Total</span>
        <span className={MILK_PACK_COLS.done}>Done</span>
      </div>
    </div>
  )
}

function MilkPackRow({ name, containers, counts, total }: Extract<PacketUnit, { kind: 'milk-pack-row' }>) {
  const serve = milkServeCol(counts.length)
  return (
    <div
      className={cn(
        'flex items-center gap-2 border-b-[0.5pt] border-[#bbbbbb] pl-[34px] pr-1 text-[11.5px]',
        rowPad(),
      )}>
      <span className={cn(MILK_PACK_COLS.milk, 'font-semibold')}>{name}</span>
      <span className={cn(MILK_PACK_COLS.containers, 'text-[11px]')}>
        {containers.map((line, i) => (
          <ContainerText key={i} line={line} />
        ))}
      </span>
      {counts.map((value, i) => (
        <span key={i} className={cn(serve, 'tabular-nums')}>
          {count(value)}
        </span>
      ))}
      <span className={cn(MILK_PACK_COLS.total, 'font-bold tabular-nums')}>{total.toLocaleString()}</span>
      <span className={cn(MILK_PACK_COLS.done, 'flex')}>
        <TickBox />
      </span>
    </div>
  )
}

function MilkPackTotal({ counts, total }: Extract<PacketUnit, { kind: 'milk-pack-total' }>) {
  const serve = milkServeCol(counts.length)
  return (
    <div
      className={cn('flex items-center gap-2 border-t-[1pt] border-[#1a1a1a] px-1 text-[11.5px] font-bold', rowPad())}>
      <span className={MILK_PACK_COLS.milk}>Total</span>
      <span className={MILK_PACK_COLS.containers} />
      {counts.map((value, i) => (
        <span key={i} className={cn(serve, 'tabular-nums')}>
          {count(value)}
        </span>
      ))}
      <span className={cn(MILK_PACK_COLS.total, 'tabular-nums')}>{total.toLocaleString()}</span>
      <span className={MILK_PACK_COLS.done} />
    </div>
  )
}

// --- recipes ---------------------------------------------------------------

/**
 * The recipes' section cover (Matt, Oct 20): under its title (how many recipes, the date stamp), MEALS IN THIS
 * SECTION, a row per meal with its serve day and its servings.
 */
function RecipesCover({ meals }: Extract<PacketUnit, { kind: 'recipes-cover' }>) {
  return (
    <div className="flex flex-col">
      {meals.length > 0 ? (
        <div className="pt-7">
          <p className="mb-1 border-b-[0.5pt] border-[#bbbbbb] px-1 pb-1 text-[12px] font-bold normal-case tracking-normal">
            Meals in this section
          </p>
          {meals.map((meal) => (
            <div
              key={`${meal.label}|${meal.serve}`}
              className="flex items-baseline gap-4 border-b-[0.5pt] border-[#bbbbbb] px-1 py-3">
              <span className="w-[160px] shrink-0 text-[20px] font-bold">{meal.label}</span>
              <span className="min-w-0 flex-1 text-[13px]">{meal.serve}</span>
              <span className="text-[20px] font-bold tabular-nums">{meal.servings.toLocaleString()}</span>
              <span className="w-[60px] text-[12px]">{meal.servings === 1 ? 'serving' : 'servings'}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** A column's small label over its list: "INGREDIENTS (AP)", "STEPS". */
function ListLabel({ children }: { children: ReactNode }) {
  return <p className="py-1 text-[11px] font-bold uppercase tracking-[0.8px] text-[#555555]">{children}</p>
}

/** A critical limit's warning icon, where a step's number would be. */
function LimitMark() {
  return (
    <TriangleAlert role="img" aria-label="Critical limit" className="mt-[2px] h-[12px] w-[12px]" strokeWidth={2.25} />
  )
}

/**
 * A Cook block's body (the Oct 21 canvas): the ingredients, amount first, beside the numbered steps. `compact` is
 * the tighter spacing a recipe takes to keep to one page (`preferOnePage`).
 */
/** A step's own ingredients, scaled, under its text: "4.5 lb Cheddar · 2 qt Milk". */
function StepUses({ uses }: { uses: readonly string[] | undefined }) {
  return uses && uses.length > 0 ? (
    <span className="block text-[11.5px] font-semibold text-[#555555]">{uses.join(' · ')}</span>
  ) : null
}

function RecipeBody({
  ingredients,
  method,
  uses,
  limits,
  compact,
}: Extract<PacketUnit, { kind: 'recipe-body' }> & { compact: boolean }) {
  return (
    <div className={cn('grid grid-cols-[300px_minmax(0,1fr)] items-start gap-7', compact ? 'pt-0.5' : 'pt-1')}>
      <div className="flex flex-col">
        <ListLabel>Ingredients (AP)</ListLabel>
        {ingredients.map((ing, i) => (
          <div
            key={i}
            className={cn('flex gap-3 border-b-[0.5pt] border-[#bbbbbb] text-[13px] leading-[1.3]', rowPad(compact))}>
            <span className="w-[118px] shrink-0 font-bold">{ing.amount ?? ''}</span>
            <span className="min-w-0 flex-1">{ing.name}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-col">
        <ListLabel>Steps</ListLabel>
        <ol className={cn('flex flex-col text-[13px]', compact ? 'leading-[1.25]' : 'leading-[1.35]')}>
          {method.map((step, i) => (
            <li
              key={`step|${i}`}
              className={cn('grid grid-cols-[16px_minmax(0,1fr)] gap-2.5', compact ? 'py-px' : 'py-[3px]')}>
              <span className="font-bold tabular-nums">{i + 1}.</span>
              <span>
                {step}
                <StepUses uses={uses?.[i]} />
              </span>
            </li>
          ))}
          {limits.map((limit) => (
            <li
              key={`limit|${limit}`}
              className={cn('grid grid-cols-[16px_minmax(0,1fr)] gap-2.5', compact ? 'py-px' : 'py-[3px]')}>
              <LimitMark />
              <span className="font-semibold">{limit}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}

/** "Cook · Creamy Cheddar Mac" over its 1pt rule, "Yield: 30 servings | Portion: 1½ cup" on the right. */
function CookHead({
  dish,
  yieldLine,
  compact,
}: Extract<PacketUnit, { kind: 'recipe-cook-head' }> & { compact: boolean }) {
  return (
    <div className={compact ? BAND_SPACE.none : BAND_SPACE.group}>
      <div className={cn('flex items-baseline justify-between gap-3 pb-1', RULE_BOTTOM)}>
        <span className={BAND_TITLE}>Cook · {dish}</span>
        {yieldLine ? <span className="text-[13px] font-semibold text-[#555555]">{yieldLine}</span> : null}
      </div>
    </div>
  )
}

/** A HACCP row (the Oct 21 canvas): the critical limit large on the left; what to do, then the corrective action. */
function LimitRow({
  limit,
  lead,
  corrective,
  compact,
}: Extract<PacketUnit, { kind: 'recipe-limit' }> & { compact: boolean }) {
  return (
    <div className={cn('flex gap-4 border-b-[0.5pt] border-[#bbbbbb]', rowPad(compact))}>
      <span className="w-[130px] shrink-0 text-[13px] font-extrabold leading-[1.3]">{limit ?? ''}</span>
      <p className={cn('min-w-0 flex-1 text-[13px]', compact ? 'leading-[1.25]' : 'leading-[1.35]')}>
        {lead ? (/[.!?]$/.test(lead) ? lead : `${lead}.`) : null}
        {corrective ? <span className="font-semibold"> {corrective}</span> : null}
      </p>
    </div>
  )
}

/**
 * The Allergens row's words. "No major allergens." only when every food on the page has full evidence; otherwise a
 * missing list never reads as "none" — the chef is sent to the labels.
 */
export function allergenWords(contains: readonly string[] | null, complete: boolean): string {
  if (contains) {
    const list = `Contains ${contains.join(', ')}.`
    return complete ? list : `${list} Other allergens may not be on file; check the product labels.`
  }
  return complete ? 'No major allergens.' : 'Allergens not on file; check the product labels.'
}

/** The Allergens row: "Contains Milk, Wheat." in plain text, then the dietary tags outlined. */
function AllergensRow({
  contains,
  diets,
  complete,
  compact,
}: Extract<PacketUnit, { kind: 'recipe-allergens' }> & { compact: boolean }) {
  return (
    <div className={cn('flex gap-4 border-b-[0.5pt] border-[#bbbbbb]', rowPad(compact))}>
      <span className="w-[130px] shrink-0 text-[13px] font-extrabold leading-[1.3]">Allergens</span>
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-[13px] leading-[1.35]">
        <span>{allergenWords(contains, complete)}</span>
        <DietTags diets={diets} />
      </span>
    </div>
  )
}

/** The Pack table's columns, the Production Totals' (the Oct 21 canvas). */
const RECIPE_PACK_COLS = {
  type: 'min-w-0 flex-1',
  temp: 'w-[92px] shrink-0',
  portion: 'w-[92px] shrink-0',
  total: 'w-[56px] shrink-0',
  pack: 'w-[170px] shrink-0',
  stops: 'w-[80px] shrink-0',
}

function RecipePackHead() {
  return (
    <div className={TABLE_HEADER_CLASS}>
      <span className={RECIPE_PACK_COLS.type}>Pack type</span>
      <span className={RECIPE_PACK_COLS.temp}>Deliver temp</span>
      <span className={RECIPE_PACK_COLS.portion}>Portion</span>
      <span className={RECIPE_PACK_COLS.total}>Total</span>
      <span className={RECIPE_PACK_COLS.pack}>Pack</span>
      <span className={RECIPE_PACK_COLS.stops}>Stops</span>
    </div>
  )
}

/** "Unitized" over "Mac + Broccoli"; its temperature, portion, total, containers and stops. */
function RecipePackRow({
  packType,
  name,
  tags,
  meal,
  temperature,
  portion,
  total,
  containers,
  stops,
  compact,
}: Extract<PacketUnit, { kind: 'recipe-pack-row' }> & { compact: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center border-b-[0.5pt] border-[#bbbbbb] text-[13px] leading-[1.3] [&>span]:px-2',
        rowPad(compact),
      )}>
      <span className={cn(RECIPE_PACK_COLS.type, 'flex flex-col gap-px')}>
        <span className="font-bold">{packType}</span>
        <span className="text-[11.5px] text-[#555555]">
          {name}
          <RowTags tags={tags} />
        </span>
        {meal ? <span className="text-[11.5px] font-semibold">{meal}</span> : null}
      </span>
      <span className={RECIPE_PACK_COLS.temp}>
        <TempTag temperature={temperature} />
      </span>
      <span className={RECIPE_PACK_COLS.portion}>{portion ?? '–'}</span>
      <span className={cn(RECIPE_PACK_COLS.total, 'font-bold tabular-nums')}>{total.toLocaleString()}</span>
      <span className={cn(RECIPE_PACK_COLS.pack, 'text-[12px]')}>
        {containers.map((line, i) => (
          <ContainerText key={i} line={line} />
        ))}
      </span>
      <span className={cn(RECIPE_PACK_COLS.stops, 'tabular-nums')}>{stops}</span>
    </div>
  )
}

/** Abner, Oct 18: Containers before the quantities, "Count" and "Each serving" renamed "Total" and "Unit". */
const BULK_COLS = {
  stop: 'w-[26px] shrink-0',
  site: 'min-w-0 flex-1',
  containers: 'w-[178px] shrink-0',
  total: 'w-[52px] shrink-0',
  unit: 'w-[90px] shrink-0',
  temp: 'w-[62px] shrink-0',
  done: 'w-[34px] shrink-0',
}

function RecipeBulkHead({ compact }: { compact: boolean }) {
  return (
    <div>
      <div className={compact ? TABLE_HEADER_CLASS.replace('[&>span]:pt-1 ', '[&>span]:pt-0.5 ') : TABLE_HEADER_CLASS}>
        <span className={BULK_COLS.stop}>Stop</span>
        <span className={BULK_COLS.site}>Organization · Site</span>
        <span className={BULK_COLS.containers}>Containers</span>
        <span className={BULK_COLS.total}>Total</span>
        <span className={BULK_COLS.unit}>Unit</span>
        <span className={BULK_COLS.temp}>Temp</span>
        <span className={BULK_COLS.done}>Done</span>
      </div>
    </div>
  )
}

/** A stop's row: the site with its flags, and under it the meal where the page names one (a later day's semibold). */
function RecipeBulkRow({
  stop,
  tags,
  meal,
  containers,
  total,
  unit,
  temperature,
  compact,
}: Extract<PacketUnit, { kind: 'recipe-bulk-row' }> & { compact: boolean }) {
  return (
    <div
      className={cn('flex items-center gap-2 border-b-[0.5pt] border-[#bbbbbb] px-1 text-[11.5px]', rowPad(compact))}>
      <span className={BULK_COLS.stop}>
        <StopTile number={stop.number} size="md" />
      </span>
      <span className={cn(BULK_COLS.site, 'flex flex-col')}>
        <span className="font-semibold">
          {stop.label}
          <RowTags tags={tags} />
        </span>
        {meal ? (
          <span className={cn('text-[10.5px] leading-tight', meal.strong && 'font-semibold')}>{meal.text}</span>
        ) : null}
      </span>
      <span className={cn(BULK_COLS.containers, 'text-[11px]')}>
        {containers.map((line, i) => (
          <ContainerText key={i} line={line} />
        ))}
      </span>
      <span className={cn(BULK_COLS.total, 'font-bold tabular-nums')}>{total.toLocaleString()}</span>
      <span className={BULK_COLS.unit}>{unit}</span>
      <span className={BULK_COLS.temp}>
        <TempTag temperature={temperature} />
      </span>
      <span className={cn(BULK_COLS.done, 'flex')}>
        <TickBox />
      </span>
    </div>
  )
}

function RecipeStopBlock({
  stop,
  meal,
  rows,
  compact,
}: Extract<PacketUnit, { kind: 'recipe-stop' }> & { compact: boolean }) {
  return (
    <div>
      <p className="flex items-center gap-2 text-[12px]">
        <StopTile number={stop.number} size="md" />
        <span>
          For <span className="font-semibold">{stop.label}</span>
        </span>
        <span className="text-[10.5px]">{meal}</span>
      </p>
      <div className={cn(TABLE_HEADER_CLASS, compact ? 'mt-0.5' : 'mt-1')}>
        <span className="w-[34px] shrink-0">Check</span>
        <span className="w-[42px] shrink-0">Count</span>
        <span className="w-[190px] shrink-0">Package type</span>
        <span className="min-w-0 flex-1">Content</span>
      </div>
      {rows.map((row, i) => (
        <div
          key={i}
          className={cn(
            'flex items-start gap-2 border-b-[0.5pt] border-[#bbbbbb] px-1 text-[11.5px]',
            rowPad(compact),
          )}>
          <span className="w-[34px] shrink-0 pt-0.5">
            <TickBox />
          </span>
          <span className="w-[42px] shrink-0 font-bold tabular-nums">{row.count}</span>
          <span className="w-[190px] shrink-0">{row.packageType}</span>
          <span className="min-w-0 flex-1">
            {row.contents.map((item, j) => (
              <span key={j} className={cn('block', item.mine && 'font-bold')}>
                <span aria-hidden className="mr-1">
                  ↳
                </span>
                {item.amount ? `${item.amount} ` : ''}
                {item.name}
              </span>
            ))}
          </span>
        </div>
      ))}
    </div>
  )
}

// --- stop summaries ------------------------------------------------------------

/**
 * Abner, Oct 18: the pack list's containers and the stop sheet's temperatures in one table; Matt, Oct 20: a wider
 * Containers column, dotted column lines to write in instead of boxes. The Oct 21 mockup's order: Item, Deliver temp,
 * Portion, Serv., then the containers; its one Serv. count replaces Total and a Unit that always read "Servings".
 * Every fixed column is as narrow as its label allows, so the Item column has room for a name on one line (Oct 24: a
 * 142px Item column wrapped most names, and a busy stop ran each copy onto a second sheet).
 */
const STOP_COLS = {
  item: 'min-w-0 flex-1',
  temp: 'w-[88px] shrink-0',
  portion: 'w-[72px] shrink-0',
  total: 'w-[40px] shrink-0',
  containers: 'w-[136px] shrink-0',
}

/**
 * The stop table's labels, spaced as its rows are (`StopRow`: a 2-unit gap, no padding inside a column) so each label
 * sits over its values; the shared header pads inside every column and has no gap, which drifted them apart.
 */
const STOP_HEADER_CLASS = cn(TABLE_HEADER_CLASS.replace('[&>span]:px-2 ', ''), 'gap-2 px-1')

/** A write-in column's width: the two °F columns (and the school's Time), then the tick. */
const WRITE_COL = { reading: 'w-[64px]', tick: 'w-[52px]' } as const

/**
 * A label over a dotted write-in column, on the same line as the table's other labels (Sheila, Oct 23 V4: they sat
 * lower than ITEM, and the school's lunch table stacked a RECEIVED line over its own that breakfast did not).
 */
function WriteLabel({ width, children }: { width: string; children: ReactNode }) {
  return <span className={cn(width, 'shrink-0 pl-1.5 normal-case tracking-normal')}>{children}</span>
}

/**
 * A meal's column labels. The school copy's own: Received °F, Time and OK; the kitchen copy's: Kitchen °F,
 * Drop-off °F and Received (the temperature photo reads those words). One row, the same on every table.
 */
function StopLabels({ copy }: { copy: StopCopy }) {
  return (
    <div className={STOP_HEADER_CLASS}>
      <span className={STOP_COLS.item}>Item</span>
      <span className={STOP_COLS.temp}>{copy === 'kitchen' ? 'Deliver temp' : 'Serve temp'}</span>
      <span className={STOP_COLS.portion}>Portion</span>
      <span className={STOP_COLS.total}>Serv.</span>
      <span className={STOP_COLS.containers}>Containers</span>
      {copy === 'kitchen' ? (
        <>
          <WriteLabel width={WRITE_COL.reading}>Kitchen °F</WriteLabel>
          <WriteLabel width={WRITE_COL.reading}>Drop-off °F</WriteLabel>
          <WriteLabel width={WRITE_COL.tick}>Received</WriteLabel>
        </>
      ) : (
        <>
          <WriteLabel width={WRITE_COL.reading}>Received °F</WriteLabel>
          <WriteLabel width={WRITE_COL.reading}>Time</WriteLabel>
          <WriteLabel width={WRITE_COL.tick}>OK</WriteLabel>
        </>
      )}
    </div>
  )
}

/** A dotted write-in cell, the row's full height; `children` for "n/a" or the tick. */
function WriteCell({ width, compact, children }: { width: string; compact: boolean; children?: ReactNode }) {
  return (
    <span className={cn(width, 'flex shrink-0 items-center self-stretch pl-1.5', compact ? '-my-0.5' : '-my-1')}>
      {children ?? <span aria-hidden className="mr-1 mt-3 w-full border-b-[0.5pt] border-[#bbbbbb]" />}
    </span>
  )
}

/**
 * P9: a line of the stop summary. Ambient food takes no temperature: "n/a" in
 * the school's °F, in both of the kitchen's °F columns.
 */
function StopRow({ row, copy, compact }: { row: StopRowData; copy: StopCopy; compact: boolean }) {
  const na = row.noTemps ? <span className="text-[10.5px]">n/a</span> : null
  return (
    <div
      className={cn(
        'flex items-center gap-2 border-b-[0.5pt] border-[#bbbbbb] px-1 text-[12.5px]',
        rowPad(compact),
        compact ? 'leading-[1.15]' : 'leading-[1.25]',
      )}>
      <span className={STOP_COLS.item}>
        <span className="font-bold">{row.name}</span>
        <DietTags diets={row.diets} />
        <RowTags tags={row.tags} />
      </span>
      <span className={cn(STOP_COLS.temp, 'flex flex-col items-start gap-0.5')}>
        <TempTag temperature={copy === 'kitchen' ? row.temperature : row.serveTemperature} />
        {copy === 'school' && row.heatOnSite ? <span className="text-[10px] font-semibold">Heat on site</span> : null}
      </span>
      <span className={cn(STOP_COLS.portion, 'whitespace-pre-line text-[12px] leading-[14px]')}>
        {row.portion ?? '—'}
      </span>
      <span className={cn(STOP_COLS.total, 'font-semibold tabular-nums')}>{row.total.toLocaleString()}</span>
      <span className={cn(STOP_COLS.containers, 'text-[11px] leading-[13px] text-[#555555]')}>
        {row.containers.map((line, i) => (
          <ContainerText key={i} line={line} />
        ))}
      </span>
      <WriteCell width={WRITE_COL.reading} compact={compact}>
        {na}
      </WriteCell>
      <WriteCell width={WRITE_COL.reading} compact={compact}>
        {copy === 'kitchen' ? na : null}
      </WriteCell>
      <WriteCell width={WRITE_COL.tick} compact={compact}>
        <TickBox />
      </WriteCell>
    </div>
  )
}

/**
 * The stop sheet's head (the Oct 21 canvas): the date stamp, the stop's number in a black badge of the same height,
 * "STOP 1 OF 5 · KITCHEN COPY" over the site, and under it the organization, address and (kitchen copy) the contact;
 * then one outlined box, the delivery window styled like the date beside the copy's instructions.
 */
function StopHead({
  copy,
  stop,
  eyebrow,
  dateBox,
  window,
  windowNote,
  address,
  contactName,
  contactPhone,
  instructionsLabel,
  instructions,
  warning,
}: Extract<PacketUnit, { kind: 'stop-head' }>) {
  const line = [stop.organization, address, contactPhone, contactName].filter(Boolean).join(' · ')
  return (
    <div className="flex flex-col gap-2 pb-0.5 pt-2.5">
      <div className="flex items-center gap-3.5">
        <DateBox box={dateBox} />
        <StopTile number={stop.number} size="xl" />
        <div data-packet-deliver-to="" className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <p className="text-[12px] font-bold tracking-[1px] text-[#555555]">{eyebrow}</p>
          <h1 className="text-[23px] font-extrabold leading-[1.1]">{stop.site}</h1>
          {line ? <p className="text-[13px] text-[#555555]">{line}</p> : null}
          {warning ? <Warning text={warning} /> : null}
        </div>
      </div>
      <div
        data-packet-window-box=""
        className="flex items-center gap-4 rounded-lg border-[1pt] border-[#1a1a1a] px-3.5 py-2">
        <span className="flex shrink-0 flex-col border-r-[0.5pt] border-[#bbbbbb] pr-4">
          <span className="text-[12px] font-bold tracking-[1px]">
            {copy === 'kitchen' ? 'WINDOW' : 'DELIVERY WINDOW'}
          </span>
          <span className="whitespace-nowrap text-[22px] font-extrabold leading-[1.1]">{window ?? '–'}</span>
          {windowNote ? <span className="text-[11.5px] text-[#555555]">{windowNote}</span> : null}
        </span>
        <p className="min-w-0 flex-1 text-[13px] leading-[1.4]">
          <span className="font-bold">{instructionsLabel}:</span>{' '}
          {instructions.length > 0 ? instructions.join(' ') : '–'}
        </p>
      </div>
    </div>
  )
}

// --- the unit --------------------------------------------------------------

export interface UnitViewProps {
  unit: PacketUnit
  /** A group band or repeat unit printed again atop a continuation page. */
  continued: boolean
  /** The section prints at compact spacing (stop summaries). */
  compact: boolean
}

export function UnitView({ unit, continued, compact }: UnitViewProps) {
  const render = usePacketRender()
  switch (unit.kind) {
    case 'band': {
      if (unit.id.includes('|production|') && (unit.text === 'MEALS' || unit.text === 'SIDES')) {
        return <div className="border-b-[0.5pt] border-[#bbbbbb] px-3 py-1 text-[10px] font-semibold">{unit.text}</div>
      }
      const refs =
        unit.refs && unit.refs.length > 0 ? (
          <>
            {unit.note ? ': ' : null}
            {unit.refs.map((ref, i) => {
              const page = render.pageOf([ref.sectionId])
              return (
                <Fragment key={ref.sectionId}>
                  {i > 0 ? '; ' : null}
                  {ref.label}
                  {page ? `, p. ${page}` : ''}
                </Fragment>
              )
            })}
          </>
        ) : null
      return (
        <SectionBand
          text={unit.text}
          note={unit.note}
          level={unit.level}
          continued={continued}
          extra={refs}
          space={unit.space}
          tone={unit.tone}
        />
      )
    }
    case 'heading':
      if (unit.text === 'Production') {
        return null
      }
      if (unit.id.includes('|production|') || unit.id.includes('|milk|')) {
        const separate = /pack separately/i.test(unit.text)
        const title = unit.text.replace(/\s*·\s*pack separately/i, '')
        const parts = title.split(/\s*·\s*Serve\s+|\s+(?=(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)\b)/i)
        const meal = parts[0]
        const serve = parts.slice(1).join(' ') || 'Today'
        return (
          <div className={BAND_SPACE.group}>
            <h2 className={cn('flex items-baseline justify-between gap-3 pb-1', RULE_BOTTOM)}>
              <span className={BAND_TITLE}>{meal}</span>
              <span className="flex items-baseline gap-2 text-[13px] font-semibold text-[#555555]">
                Serve {/^today$/i.test(serve) ? 'today' : serve}
                {separate ? (
                  <span className="inline-block whitespace-nowrap rounded-[5px] border-[1pt] border-[#1a1a1a] px-1.5 text-[11px] font-bold text-[#1a1a1a]">
                    Pack separately
                  </span>
                ) : null}
              </span>
            </h2>
          </div>
        )
      }
      return <p className="pt-2.5 text-[20px] font-bold leading-tight">{unit.text}</p>
    case 'cover-notice':
      return <CoverNotice {...unit} compact={compact} />
    case 'cover-stops-head':
      return <CoverStopsHead />
    case 'cover-stop':
      return <CoverStop {...unit} compact={compact} />
    case 'cover-toc':
      return <CoverToc {...unit} compact={compact} />
    case 'pull-key':
      return <PullKey {...unit} />
    case 'pull-intro':
      return <PullIntro {...unit} />
    case 'pull-head':
      return <PullHead {...unit} />
    case 'pull-row':
      return <PullRow {...unit} />
    case 'matrix-head':
      return <MatrixHead {...unit} />
    case 'matrix-row':
      return <MatrixRow {...unit} />
    case 'matrix-subtotal':
      return <MatrixSubtotal {...unit} />
    case 'matrix-staged':
      return null
    case 'section-note':
      return <SectionNote {...unit} />
    case 'shop-head':
      return unit.space ? <div className={BAND_SPACE[unit.space]} /> : null
    case 'shop-row':
      return (
        <div className={cn('flex items-center gap-2 border-b-[0.5pt] border-[#bbbbbb] px-3 text-[12px]', rowPad())}>
          <span className={SHOP_COLS.tick}>
            <TickBox size="sm" />
          </span>
          <span className={cn(SHOP_COLS.item, 'font-medium')}>
            {unit.name}
            {unit.brand?.trim() ? <em className="ml-2 font-normal italic text-[#555555]">{unit.brand}</em> : null}
            {unit.tag ? <RowTags tags={[unit.tag]} /> : null}
          </span>
          <span className={cn(SHOP_COLS.buy, 'font-semibold tabular-nums')}>{unit.buy}</span>
        </div>
      )
    case 'milk-shop-head':
      return <MilkShopHead />
    case 'milk-shop-row':
      return <MilkShopRow {...unit} />
    case 'milk-served':
      return <MilkServed {...unit} />
    case 'milk-pack-head':
      return <MilkPackHead {...unit} />
    case 'milk-pack-stop':
      return (
        <div className="flex items-center gap-2 border-b-[0.5pt] border-[#bbbbbb] px-1 pb-1 pt-2">
          <StopTile number={unit.stop.number} size="md" />
          <StopName stop={unit.stop} />

          <StopTypeTag type={unit.stop.type} />
        </div>
      )
    case 'milk-pack-row':
      return <MilkPackRow {...unit} />
    case 'milk-pack-total':
      return <MilkPackTotal {...unit} />
    case 'recipes-cover':
      return <RecipesCover {...unit} />
    case 'recipe-limits':
      return (
        <div className={BAND_SPACE.group}>
          <div className="flex items-stretch border-[1pt] border-[#1a1a1a]">
            <span className="flex shrink-0 items-center border-r-[0.5pt] border-[#bbbbbb] px-2.5 text-[10.5px] font-bold normal-case tracking-normal text-black">
              Critical limits
            </span>
            <span className="flex flex-1 flex-wrap items-center gap-x-5 gap-y-0.5 px-3 py-1.5 text-[11.5px] font-semibold">
              {unit.limits.map((limit) => (
                <span key={limit}>{limit}</span>
              ))}
            </span>
          </div>
        </div>
      )
    case 'recipe-body':
      return <RecipeBody {...unit} compact={compact} />
    case 'recipe-ingredient':
      return (
        <div className={cn('flex gap-2 border-b-[0.5pt] border-[#bbbbbb] px-1 text-[11.5px]', rowPad(compact))}>
          <span className="w-[30%] shrink-0 font-bold">{unit.amount ?? ''}</span>
          <span className="min-w-0 flex-1">{unit.name}</span>
        </div>
      )
    case 'recipe-step':
      return (
        <p
          className={cn(
            'grid grid-cols-[18px_minmax(0,1fr)] px-1 text-[11.5px] leading-snug',
            compact ? 'pt-0.5' : 'pt-1',
          )}>
          {unit.number === null ? <LimitMark /> : <span className="font-bold tabular-nums">{unit.number}.</span>}
          <span className={unit.number === null ? 'font-semibold' : undefined}>
            {unit.text}
            <StepUses uses={unit.uses} />
          </span>
        </p>
      )
    case 'recipe-tip':
      return (
        <p className={cn('px-1 text-[13px]', compact ? 'py-px leading-[1.25]' : 'py-[3px] leading-[1.35]')}>
          · {unit.text}
        </p>
      )
    case 'recipe-cook-head':
      return <CookHead {...unit} compact={compact} />
    case 'recipe-limit':
      return <LimitRow {...unit} compact={compact} />
    case 'recipe-allergens':
      return <AllergensRow {...unit} compact={compact} />
    case 'recipe-pack-head':
      return <RecipePackHead />
    case 'recipe-pack-row':
      return <RecipePackRow {...unit} compact={compact} />
    case 'recipe-bulk-head':
      return <RecipeBulkHead compact={compact} />
    case 'recipe-bulk-row':
      return <RecipeBulkRow {...unit} compact={compact} />
    case 'recipe-bulk-total':
      return (
        <div
          className={cn(
            'flex items-center gap-2 border-b-[0.5pt] border-[#bbbbbb] px-1 text-[11.5px] font-semibold',
            rowPad(compact),
          )}>
          <span className={BULK_COLS.stop} />
          <span className={BULK_COLS.site}>Total</span>
          <span className={cn(BULK_COLS.containers, 'text-[11px]')}>{unit.containers ?? ''}</span>
          <span className={cn(BULK_COLS.total, 'tabular-nums')}>{unit.total.toLocaleString()}</span>
          <span className={BULK_COLS.unit}>{unit.unit}</span>
          <span className={BULK_COLS.temp} />
          <span className={BULK_COLS.done} />
        </div>
      )
    case 'recipe-stop':
      return <RecipeStopBlock {...unit} compact={compact} />
    case 'recipe-total':
      return (
        <p className={cn('border-b-[0.5pt] border-[#bbbbbb] px-3 text-[11.5px] font-semibold', rowPad(compact))}>
          {unit.text}
        </p>
      )
    case 'recipe-haccp':
      return (
        <ul
          className={cn(
            'flex flex-col overflow-hidden border-b-[0.5pt] border-[#bbbbbb] text-[11px] leading-relaxed',
            'gap-0',
          )}>
          {unit.lines.map((line, i) => (
            <li key={i} className={cn('border-b-[0.5pt] border-[#bbbbbb] px-3 last:border-b-0', rowPad(compact))}>
              {line}
            </li>
          ))}
        </ul>
      )
    case 'stop-head':
      return <StopHead {...unit} />
    case 'stop-labels':
      return <StopLabels copy={unit.copy} />
    case 'stop-row':
      return <StopRow row={unit} copy={unit.copy} compact={compact} />
    case 'menu-notes':
      return (
        <div className="my-1.5 flex flex-col gap-1 rounded-lg border-[1pt] border-[#1a1a1a] bg-[#fafafa] px-3 py-2 text-[11px] leading-relaxed">
          {unit.notes.map((note) => (
            <p key={note.menuId}>
              <span className="font-semibold">{note.menuName}</span>
              {note.delivery ? <span className="whitespace-pre-wrap"> · Delivery: {note.delivery}</span> : null}
              {note.service ? <span className="whitespace-pre-wrap"> · Service: {note.service}</span> : null}
            </p>
          ))}
        </div>
      )
    case 'stop-signoff':
      return (
        <div className={compact ? BAND_SPACE.none : BAND_SPACE.group}>
          {/* Titled like the meal bands, over the same rule (Sheila, Oct 23 V4). */}
          <p className={cn('pb-1', BAND_TITLE, RULE_BOTTOM)}>Sign-Off</p>
          <div className={cn('flex flex-col px-1 text-[11.5px]', compact ? 'gap-1.5 pt-1.5' : 'gap-2.5 pt-2.5')}>
            {unit.copy === 'kitchen' ? (
              // The kitchen's own lines share one row with the driver's, so its box is the school's height.
              <span className="flex gap-6">
                <WriteIn label="Packed by (chef)" className="flex-[4]" />
                <WriteIn label="Time out" className="flex-[2]" />
                {unit.dropOff ? <WriteIn label="Driver" className="flex-[3]" /> : null}
              </span>
            ) : null}
            {unit.dropOff ? (
              <>
                <span className="flex gap-6">
                  <WriteIn label="Received by" className="flex-[3]" />
                  <WriteIn label="Signature" className="flex-[3]" />
                  {unit.copy === 'kitchen' ? (
                    <>
                      <WriteIn label="Date" className="flex-[2]" />
                      <WriteIn label="Time" className="flex-[2]" />
                    </>
                  ) : null}
                </span>
                {unit.copy === 'kitchen' ? null : (
                  <span className="flex gap-6">
                    <WriteIn label="Driver" className="flex-[4]" />
                    <WriteIn label="Date" className="flex-[3]" />
                    <WriteIn label="Time" className="flex-[3]" />
                  </span>
                )}
                <WriteIn label="Missing, short or out-of-temp items:" />
              </>
            ) : null}
          </div>
        </div>
      )
    case 'copy-note':
      return null
  }
}
